import React, { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { TodoBottomSheetTitle_todo$key } from "./__generated__/TodoBottomSheetTitle_todo.graphql";
import { useBottomSheetInternal } from "@gorhom/bottom-sheet";
import { useLayoutContext } from "arkh";
import Animated, {
  interpolate,
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from "react-native-reanimated";
import { useReportIssueBottomSheetWidth } from "../../useReportIssueBottomSheetLayout";

import { useReportIssueBottomSheetMainScreenFirstSnapPointHeight } from "../../useReportIssueBottomSheetMainScreenFirstSnapPoint";
import { TodoBottomSheetTitleInput } from "./TodoBottomSheetTitleInput";
import { TodoBottomSheetTitleSuggestions } from "./TodoBottomSheetTitleSuggestions";
import { useTodoEditSetTitle } from "@/src/features/TodoEdit/TodoEditNewMain/todoMutationHooks/useTodoEditSetTitle";

function TodoBottomSheetTitlePaddingContainer(props: {
  children: React.ReactNode;
  inputHeight: SharedValue<number>;
}) {
  const firstSnapPoint =
    useReportIssueBottomSheetMainScreenFirstSnapPointHeight();
  const bottomSheetWidth = useReportIssueBottomSheetWidth();
  const bottomSheetInternals = useBottomSheetInternal();
  const paddingContainerStyle = useAnimatedStyle(() => {
    return {
      height: interpolate(
        bottomSheetInternals.animatedIndex.value,
        [-1, 0, 1],
        [firstSnapPoint, firstSnapPoint, props.inputHeight.value]
      ),
    };
  });

  return (
    <Animated.View
      style={[
        {
          width: bottomSheetWidth,
        },
        paddingContainerStyle,
      ]}
    >
      {props.children}
    </Animated.View>
  );
}

export function TodoBottomSheetTitle(props: {
  todo: TodoBottomSheetTitle_todo$key;
  mode: "edit" | "create";
  setStatus: (key: string, status: "error" | "saving" | "saved") => void;
}) {
  const bottomSheetWidth = useReportIssueBottomSheetWidth();
  const bottomSheetInternals = useBottomSheetInternal();
  const todo = useFragment(
    graphql`
      fragment TodoBottomSheetTitle_todo on Todo {
        id
        title
        canWrite
        taskExecution {
          task {
            issueSuggestions {
              title
            }
          }
        }
      }
    `,
    props.todo
  );
  const [title, setTitle] = useTodoEditSetTitle(
    todo.id,
    todo.title,
    props.setStatus
  );

  const layout = useLayoutContext();

  const [inputFocused, setInputFocused] = useState(false);
  const rowHeight = layout.circle.medium + layout.space.small * 2;

  const showSuggestions = useDerivedValue(() => {
    return withTiming(title.length === 0 ? 1 : 0);
  }, [title.length]);

  const inputExpanded = useDerivedValue(() => {
    return props.mode === "create" && !inputFocused
      ? showSuggestions.value *
          Math.max(0, bottomSheetInternals.animatedIndex.value)
      : showSuggestions.value;
  });

  const inputHeight = useDerivedValue(() => {
    return interpolate(
      inputExpanded.value,
      [-1, 0, 1],
      [rowHeight, rowHeight, rowHeight * 2]
    );
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      height: inputHeight.value,
    };
  });

  const Element = useMemo(() => {
    return (
      <Animated.View
        style={[
          {
            width: bottomSheetWidth,
          },
          animatedStyle,
        ]}
      >
        <TodoBottomSheetTitleInput
          height={rowHeight}
          inputExpanded={inputExpanded}
          inputFocused={inputFocused}
          setInputFocused={setInputFocused}
          mode={props.mode}
          setTitle={setTitle}
          title={title}
          bottomSheetWidth={bottomSheetWidth}
          editable={todo.canWrite}
        />
        {todo.canWrite && title.length === 0 && todo?.taskExecution ? (
          <TodoBottomSheetTitleSuggestions
            inputExpanded={inputExpanded}
            mode={props.mode}
            showSuggestions={showSuggestions}
            setTitle={setTitle}
            suggestions={todo?.taskExecution?.task.issueSuggestions ?? []}
            height={rowHeight}
          />
        ) : null}
      </Animated.View>
    );
  }, [
    animatedStyle,
    bottomSheetWidth,
    inputExpanded,
    inputFocused,
    props.mode,
    rowHeight,
    setTitle,
    showSuggestions,
    title,
    todo.canWrite,
    todo?.taskExecution,
  ]);

  if (props.mode === "create") {
    return (
      <TodoBottomSheetTitlePaddingContainer inputHeight={inputHeight}>
        {Element}
      </TodoBottomSheetTitlePaddingContainer>
    );
  }

  return Element;
}
